import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Flex,
  Group,
  MultiSelect,
  Paper,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertTriangle, IconPlus, IconTrash } from '@tabler/icons-react';
import AggregatedReportsAPI from '../../api/AggregatedReportsApi';

// UTM field codes the backend expects under ads_tracking_1..6.
// Order mirrors campaign_report_api::tracking_network_herarical_dd.
const TRACKING_FIELD_OPTIONS: { value: string; label: string; sourceKey: string }[] = [
  { value: '1', label: 'UTM ID',       sourceKey: 'utm_id' },
  { value: '2', label: 'UTM Source',   sourceKey: 'utm_source' },
  { value: '3', label: 'UTM Medium',   sourceKey: 'utm_medium' },
  { value: '4', label: 'UTM Campaign', sourceKey: 'utm_campaign' },
  { value: '5', label: 'UTM Term',     sourceKey: 'utm_term' },
  { value: '6', label: 'UTM Content',  sourceKey: 'utm_content' },
];

const MAX_TRACKING_FILTERS = 6;

export type TrackingTagFilter = {
  id: string;
  field: string;    // '1'..'6'
  values: string[]; // base64-encoded values
};

export type CustomTagFilter = {
  id: string;
  tagId: string;    // traffic_source.id, as string
  values: string[]; // base64-encoded values
};

export type AdvancedFiltersState = {
  tracking: TrackingTagFilter[];
  custom: CustomTagFilter[];
};

export const EMPTY_ADVANCED_FILTERS: AdvancedFiltersState = {
  tracking: [],
  custom: [],
};

// Keys here mirror the campaign_report_api glob shape so the backend can pick them up unchanged.
export const advancedFiltersToApiParams = (s: AdvancedFiltersState): Record<string, any> => {
  const out: Record<string, any> = {};
  for (const t of s.tracking) {
    if (!t.field || t.values.length === 0) { continue; }
    out[`ads_tracking_${t.field}`] = t.values;
  }
  if (s.custom.length > 0) {
    const map: Record<string, string[]> = {};
    for (const c of s.custom) {
      if (!c.tagId || c.values.length === 0) { continue; }
      map[c.tagId] = c.values;
    }
    if (Object.keys(map).length > 0) { out['ads_custom_array'] = map; }
  }
  return out;
};

type DropdownOption = { value: string; label: string; group?: string };

type Props = {
  enabled: boolean;            // false → multi or zero campaigns selected; Custom Tags are disabled
  campaignId: string;          // encrypted; the single campaign Custom Tags load for
  campaignIds: string[];       // encrypted; every selected campaign, for Tracking Tags
  timeParams?: Record<string, any>; // report date range, so Tracking Tag values come from that window
  refreshKey?: number;         // bumped by Update View to reload every dropdown
  value: AdvancedFiltersState;
  onChange: (next: AdvancedFiltersState) => void;
  // Traffic Source works for one or many campaigns, so it is shown whatever the campaign count.
  trafficSourceOptions: DropdownOption[];
  trafficSourceValue: string[];
  onTrafficSourceChange: (next: string[]) => void;
};

// Normalises dropdown_data rows ({ key, value, selected? }) into Mantine options.
// Numeric labels are coerced via String() so numeric-string UTM ids keep their label.
const normalizeDropdownRows = (rows: any[]): DropdownOption[] => {
  if (!Array.isArray(rows)) { return []; }
  const out: DropdownOption[] = [];
  for (const r of rows) {
    if (!r || typeof r !== 'object') { continue; }
    const key = typeof r.key === 'string' ? r.key : '';
    const rawLabel = r.value;
    const label =
      typeof rawLabel === 'string' ? rawLabel
      : typeof rawLabel === 'number' ? String(rawLabel)
      : '';
    if (!key || key.startsWith('all_')) { continue; }
    out.push({ value: key, label: label || key });
  }
  return out;
};

// Augments `options` with placeholder entries for saved values not yet present, so
// Mantine MultiSelect doesn't drop saved selections that aren't in `data` yet.
const mergeSavedValues = (options: DropdownOption[], saved: string[]): DropdownOption[] => {
  if (!saved || saved.length === 0) { return options; }
  const known = new Set(options.map((o) => o.value));
  const out = options.slice();
  for (const v of saved) {
    if (!v || known.has(v)) { continue; }
    out.push({ value: v, label: v });
    known.add(v);
  }
  return out;
};

export const AdvancedFilters = ({
  enabled, campaignId, campaignIds, timeParams, value, onChange, refreshKey = 0, trafficSourceOptions, trafficSourceValue, onTrafficSourceChange,
}: Props) => {
  const [trackingOptionsByField, setTrackingOptionsByField] = useState<Record<string, DropdownOption[]>>({});
  const [customOptionsByTag, setCustomOptionsByTag] = useState<Record<string, { name: string; options: DropdownOption[] }>>({});

  // Tracking Tags work for any number of campaigns: values come from every selected campaign.
  const campaignIdsKey = campaignIds.join(',');
  const timeParamsKey = JSON.stringify(timeParams ?? {});
  useEffect(() => {
    if (campaignIds.length === 0) {
      setTrackingOptionsByField({});
      return;
    }
    AggregatedReportsAPI.gettrackingtagdropdown({ visiopt_campaign_id: campaignIds, type: 'custom_report', ...(timeParams ?? {}) })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || data.status !== 1 || !data.data) { return; }
        const map: Record<string, DropdownOption[]> = {};
        // Translate source_key → numeric field code.
        for (const opt of TRACKING_FIELD_OPTIONS) {
          const node = data.data[opt.sourceKey];
          if (node && Array.isArray(node.dropdown_data)) {
            map[opt.value] = normalizeDropdownRows(node.dropdown_data);
          }
        }
        setTrackingOptionsByField(map);
      })
      .catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignIdsKey, timeParamsKey, refreshKey]);

  useEffect(() => {
    if (!enabled || !campaignId) {
      setCustomOptionsByTag({});
      return;
    }
    AggregatedReportsAPI.getcustomtagdropdown({ visiopt_campaign_id: [campaignId], type: 'custom_report' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || data.status !== 1 || !data.data) { return; }
        const map: Record<string, { name: string; options: DropdownOption[] }> = {};
        for (const sourceKey of Object.keys(data.data)) {
          const node = data.data[sourceKey];
          if (!node || sourceKey === 'enabled') { continue; }
          const tagId = node.array_id != null ? String(node.array_id) : '';
          if (!tagId) { continue; }
          map[tagId] = {
            name: sourceKey,
            options: normalizeDropdownRows(Array.isArray(node.dropdown_data) ? node.dropdown_data : []),
          };
        }
        setCustomOptionsByTag(map);
      })
      .catch(() => undefined);
  }, [enabled, campaignId, refreshKey]);

  const usedTrackingFields = new Set(value.tracking.map((t) => t.field));
  const trackingFieldOptions = TRACKING_FIELD_OPTIONS.map((o) => ({ value: o.value, label: o.label }));

  const usedCustomTagIds = new Set(value.custom.map((c) => c.tagId));
  const customTagOptions = Object.entries(customOptionsByTag).map(([tagId, meta]) => ({
    value: tagId,
    label: meta.name,
  }));

  const addTrackingFilter = () => {
    if (value.tracking.length >= MAX_TRACKING_FILTERS) { return; }
    onChange({ ...value, tracking: [...value.tracking, { id: `t-${Date.now()}`, field: '', values: [] }] });
  };
  const updateTrackingFilter = (id: string, patch: Partial<TrackingTagFilter>) => {
    onChange({
      ...value,
      tracking: value.tracking.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    });
  };
  const removeTrackingFilter = (id: string) => {
    onChange({ ...value, tracking: value.tracking.filter((t) => t.id !== id) });
  };

  const addCustomFilter = () => {
    onChange({ ...value, custom: [...value.custom, { id: `c-${Date.now()}`, tagId: '', values: [] }] });
  };
  const updateCustomFilter = (id: string, patch: Partial<CustomTagFilter>) => {
    onChange({
      ...value,
      custom: value.custom.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    });
  };
  const removeCustomFilter = (id: string) => {
    onChange({ ...value, custom: value.custom.filter((c) => c.id !== id) });
  };

  return (
    <Box>
      <Text size="xs" weight={700} transform="uppercase" color="dimmed" mb="xs" sx={{ letterSpacing: 0.5 }}>
        Advanced Filters
      </Text>
      <Paper withBorder radius="md" p="md" mb="md">
        <Text size="sm" weight={600} mb="sm">Traffic Source</Text>
        <MultiSelect
          placeholder={trafficSourceOptions.length === 0 ? 'No traffic sources for the selected campaigns' : 'Select traffic sources'}
          // While All Source is selected it is the only option, as in the Campaign Report.
          data={trafficSourceOptions.some((o) => o.label === 'All Sources' && trafficSourceValue.includes(o.value))
            ? trafficSourceOptions.filter((o) => o.label === 'All Sources')
            : trafficSourceOptions}
          value={trafficSourceValue}
          onChange={(next) => {
            // All Source is exclusive: picking it clears the other picks; remove it to pick single sources again.
            const allSource = trafficSourceOptions.find((o) => o.label === 'All Sources')?.value;
            onTrafficSourceChange(allSource && next.includes(allSource) ? [allSource] : next);
          }}
          searchable
          clearable
          nothingFound="No traffic sources"
          disabled={trafficSourceOptions.length === 0 && trafficSourceValue.length === 0}
          maxDropdownHeight={280}
          withinPortal
        />
      </Paper>
      <Stack spacing="md">
        <Paper withBorder radius="md" p="md">
          <Group position="apart" mb="sm">
            <Text size="sm" weight={600}>Tracking Tags</Text>
            <Button
              size="xs"
              variant="outline"
              color="teal"
              leftIcon={<IconPlus size="0.85rem" />}
              onClick={addTrackingFilter}
              disabled={value.tracking.length >= MAX_TRACKING_FILTERS}
            >
              Add Tag ({value.tracking.length}/{MAX_TRACKING_FILTERS})
            </Button>
          </Group>
          {value.tracking.length === 0 && (
            <Text size="xs" color="dimmed">No tracking tag filters yet.</Text>
          )}
          <Stack spacing="sm">
            {value.tracking.map((row) => {
              const fieldOptions = trackingFieldOptions.filter(
                (f) => f.value === row.field || !usedTrackingFields.has(f.value),
              );
              const valueOptions = trackingOptionsByField[row.field] ?? [];
              return (
                <Flex key={row.id} gap="sm" align="flex-end" wrap="wrap">
                  <Box sx={{ minWidth: 180, flex: 1 }}>
                    <Select
                      label="Field"
                      placeholder="Select field"
                      data={fieldOptions}
                      value={row.field || null}
                      onChange={(v) => updateTrackingFilter(row.id, { field: v || '', values: [] })}
                      withinPortal
                    />
                  </Box>
                  <Box sx={{ minWidth: 240, flex: 2 }}>
                    <MultiSelect
                      label="Value"
                      placeholder={row.field ? 'Select values' : 'Pick a field first'}
                      data={mergeSavedValues(valueOptions, row.values)}
                      value={row.values}
                      onChange={(v) => updateTrackingFilter(row.id, { values: v })}
                      searchable
                      clearable
                      disabled={!row.field}
                      withinPortal
                    />
                  </Box>
                  <Button
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={() => removeTrackingFilter(row.id)}
                    leftIcon={<IconTrash size="0.85rem" />}
                  >
                    Remove
                  </Button>
                </Flex>
              );
            })}
          </Stack>
        </Paper>
        {!enabled && (
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertTriangle size="1rem" />}
            sx={{ padding: '8px 12px' }}
          >
            <Text size="xs">
              Custom Tags are only available when a single campaign is selected.
            </Text>
          </Alert>
        )}
        {enabled && (
          <Paper withBorder radius="md" p="md">
            <Group position="apart" mb="sm">
              <Text size="sm" weight={600}>Custom Tags</Text>
              <Button
                size="xs"
                variant="outline"
                color="teal"
                leftIcon={<IconPlus size="0.85rem" />}
                onClick={addCustomFilter}
                disabled={customTagOptions.length === 0}
              >
                Add Tag
              </Button>
            </Group>
            {customTagOptions.length === 0 && (
              <Text size="xs" color="dimmed">No custom tags configured for this campaign.</Text>
            )}
            <Stack spacing="sm">
              {value.custom.map((row) => {
                const tagOptions = customTagOptions.filter(
                  (t) => t.value === row.tagId || !usedCustomTagIds.has(t.value),
                );
                const valueOptions = row.tagId ? (customOptionsByTag[row.tagId]?.options ?? []) : [];
                return (
                  <Flex key={row.id} gap="sm" align="flex-end" wrap="wrap">
                    <Box sx={{ minWidth: 180, flex: 1 }}>
                      <Select
                        label="Tag Name"
                        placeholder="Select tag"
                        data={tagOptions}
                        value={row.tagId || null}
                        onChange={(v) => updateCustomFilter(row.id, { tagId: v || '', values: [] })}
                        withinPortal
                      />
                    </Box>
                    <Box sx={{ minWidth: 240, flex: 2 }}>
                      <MultiSelect
                        label="Value"
                        placeholder={row.tagId ? 'Select values' : 'Pick a tag first'}
                        data={mergeSavedValues(valueOptions, row.values)}
                        value={row.values}
                        onChange={(v) => updateCustomFilter(row.id, { values: v })}
                        searchable
                        clearable
                        disabled={!row.tagId}
                        withinPortal
                      />
                    </Box>
                    <Button
                      variant="subtle"
                      color="red"
                      size="sm"
                      onClick={() => removeCustomFilter(row.id)}
                      leftIcon={<IconTrash size="0.85rem" />}
                    >
                      Remove
                    </Button>
                  </Flex>
                );
              })}
            </Stack>
          </Paper>
        )}
        <Divider />
        <Flex justify="flex-end">
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            onClick={() => {
              // Clears every advanced filter, Traffic Source included.
              onChange(EMPTY_ADVANCED_FILTERS);
              onTrafficSourceChange([]);
            }}
          >
            Clear Advanced Filters
          </Button>
        </Flex>
      </Stack>
    </Box>
  );
};
